import { useEffect, useState } from 'react';

import FiltersDisclosure from '~/shared/components/filters/FiltersDisclosure';
import { SelectOption, Account } from '~/shared/utils/types';
import Typography from '~/shared/components/Typography';
import Switch from '~/shared/components/Switch';
import SearchAccount from '~/shared/components/filters/search/SearchAccount';

import SearchRuleID from './SearchRuleID';

interface FilterPanelContentProps {
  panelOpen?: boolean;
  pendingAutoscaleState: string[];
  setPendingAutoscaleState: (state: string[]) => void;
  pendingRuleIDFilters: SelectOption[];
  setPendingRuleIDFilters: (filters: SelectOption[]) => void;
  pendingAccountFilters: SelectOption[];
  setPendingAccountFilters: (filters: SelectOption[]) => void;
  showActiveRulesOnly: boolean;
  setShowActiveRulesOnly: (activeOnly: boolean) => void;
  isOrgView?: boolean;
  accounts?: Account[];
}

export default function FilterPanelContent({
  panelOpen = false,
  // pendingAutoscaleState,
  // setPendingAutoscaleState,
  pendingRuleIDFilters,
  setPendingRuleIDFilters,
  pendingAccountFilters,
  setPendingAccountFilters,
  showActiveRulesOnly,
  setShowActiveRulesOnly,
  isOrgView = false,
  accounts = [],
}: FilterPanelContentProps) {
  const [isAccountDisclosureOpen, setIsAccountDisclosureOpen] = useState(false);
  const [isRuleIDDisclosureOpen, setIsRuleIDDisclosureOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!panelOpen) {
      setHasInitialized(false);
      return;
    }

    if (hasInitialized) {
      return;
    }

    // Check if disclosures should be open based on filters
    const shouldOpenAccount = pendingAccountFilters.length > 0;
    const shouldOpenRuleID = pendingRuleIDFilters.length > 0;

    // const hasAnyOpen =
    //   isAccountDisclosureOpen || isRuleIDDisclosureOpen || shouldOpenAccount || shouldOpenRuleID;

    if (shouldOpenAccount) {
      setIsAccountDisclosureOpen(true);
    }
    if (shouldOpenRuleID) {
      setIsRuleIDDisclosureOpen(true);
    }

    // No default open disclosure - user must manually open the ones they want

    setHasInitialized(true);
  }, [
    panelOpen,
    hasInitialized,
    pendingAccountFilters.length,
    pendingRuleIDFilters?.length,
    isAccountDisclosureOpen,
    isRuleIDDisclosureOpen,
  ]);

  const handleClearRuleIDFilters = () => setPendingRuleIDFilters([]);

  return (
    <div className="flex-1 overflow-y-auto pb-4">
      <ActiveRulesToggle
        activeOnly={showActiveRulesOnly}
        setActiveOnly={() => setShowActiveRulesOnly(!showActiveRulesOnly)}
      />

      {isOrgView && accounts.length > 0 && (
        <FiltersDisclosure
          title="Account"
          count={pendingAccountFilters.length > 0 ? pendingAccountFilters.length : undefined}
          className="px-10 border-b border-gray-300"
          open={isAccountDisclosureOpen}
          onOpenChange={setIsAccountDisclosureOpen}
          onClearFilters={() => setPendingAccountFilters([])}
        >
          <SearchAccount
            filters={pendingAccountFilters}
            setFilters={setPendingAccountFilters}
            accounts={accounts}
            isDisclosureOpen={isAccountDisclosureOpen}
          />
        </FiltersDisclosure>
      )}

      <FiltersDisclosure
        title="Rule ID"
        count={pendingRuleIDFilters.length > 0 ? pendingRuleIDFilters?.length : undefined}
        className="px-10 border-b border-gray-300"
        open={isRuleIDDisclosureOpen}
        onOpenChange={setIsRuleIDDisclosureOpen}
        onClearFilters={handleClearRuleIDFilters}
      >
        <SearchRuleID
          initialSelections={pendingRuleIDFilters}
          onSelectionsChange={setPendingRuleIDFilters}
          isDisclosureOpen={isRuleIDDisclosureOpen}
        />
      </FiltersDisclosure>
    </div>
  );
}

function ActiveRulesToggle({
  activeOnly,
  setActiveOnly,
}: {
  activeOnly: boolean;
  setActiveOnly: (activeOnly: boolean) => void;
}) {
  return (
    <div className="flex flex-col px-10 py-3 gap-3 border-y border-gray-300">
      <Typography fontWeight={600} variant="p2-semi" color="font-family-outfit text-primary-900 ">
        Active rules
      </Typography>
      <div className="flex justify-between items-center">
        <Typography fontWeight={400} variant="p2-semi" color="font-family-inter text-primary-900 ">
          Show active rules only
        </Typography>
        <Switch checked={activeOnly} onChange={() => setActiveOnly(!activeOnly)} />
      </div>
    </div>
  );
}
